import { createSlice } from '@reduxjs/toolkit';

import socket from '../../utils/socket';

import { CLOCKING_TYPES } from '../../constants/clocking-types';
import { queryClient } from '../../providers/queryClient';
import { MCE_EVENTS_QUERY_KEY } from '../../hooks/api/useMceEvents';
const initialState = {
  isLoading: false,
  isLoadingClocking: false,
  error: false,
  hasSiteRights: true,
  comments: [],
  shiftSelected: {},
  shiftSite: {},
  shiftResource: {},
  shiftContract: {},
  resources: {},
  sites: {},
  contracts: {},
  clockings: {},
  breaks: []
};

const slice = createSlice({
  name: 'shifts',
  initialState,
  reducers: {
    // LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    startLoadingClocking(state) {
      state.isLoadingClocking = true;
    },

    // ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.isLoadingClocking = false;
      state.error = action.payload;
    },

    getShiftInfos(state, action) {
      state.shiftResource = action.payload.resources.infos;
      state.hasRights = action.payload.resources.hasRights;
      state.shiftSite = action.payload.sites;
      state.shiftContract = action.payload.contracts;
      state.clockings = action.payload.clockings;
      state.comments = action.payload.comments;
      state.breaks = action.payload.breaks;
      state.hasSiteRights = action.payload.hasRightsSite ?? true;
      state.shiftSelected = {
        ...state.shiftSelected,
        ...action.payload.shift
      };
      state.isLoading = false;
    },

    validateBreak(state, action) {
      state.breaks = action.payload.breaks;
      state.isLoadingClocking = false;
    },
    validatePdsFds(state, action) {
      state.shiftSelected = { ...state.shiftSelected, ...action.payload.shift };
      state.breaks = action.payload.breaks;
      state.isLoadingClocking = false;
    },

    setShiftSelected(state, action) {
      state.shiftSelected = action.payload;
    },

    validateArrivedAt(state, action) {
      state.shiftSelected = { ...state.shiftSelected, ...action.payload.shift };
      state.isLoadingClocking = false;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const { setShiftSelected } = slice.actions;

export function getShiftInfos({
  siteId = null,
  resourceId = null,
  contractId = null,
  shiftId = null,
  month = null,
  callback
}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.get('/shift/infos', {
        resourceId,
        siteId,
        contractId,
        month,
        shiftId
      });
      if (callback) {
        callback(result);
      }
      dispatch(slice.actions.getShiftInfos(result));
    } catch (error) {
      console.error('error', error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function validateBreak({ shift, type, callback }) {
  // type: 2 = breakStart, 3 = breakEnd
  return async (dispatch) => {
    dispatch(slice.actions.startLoadingClocking());
    try {
      const { id, profile_id: profileId, site_id: siteId } = shift;
      const params = { type, vacation_id: id, profileId, siteId };
      const result = await socket.post('/breaks', params);
      dispatch(slice.actions.validateBreak(result));
      callback(null, result.breaks, type);
    } catch (error) {
      callback({ message: Array.isArray(error.errors) ? error.errors[0] : error.errors });
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function validatePdsFds({
  shift,
  isPds,
  callback,
  position,
  isForced = 0,
  outsideArea = 0,
  device = null,
  clockingAnomalyReasonId = null,
  comment = null,
  managerId = null,
  isPreClocking = false
}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoadingClocking());
    try {
      let type = isPds ? 0 : 1;
      if (isPreClocking) {
        type = 4;
      }
      const params = {
        id: shift.id,
        isPds: isPreClocking ? null : isPds,
        isPreClocking,
        latitude: position?.latitude ? position.latitude : null,
        longitude: position?.longitude ? position.longitude : null,
        type,
        is_forced: isForced,
        is_outside_area: outsideArea,
        vacation_id: shift.id,
        clocking_anomaly_reason_id: clockingAnomalyReasonId,
        comment,
        manager_id: managerId
      };

      if (isPds) {
        params.datetime_start = shift.datetime_start;
        params.datetime_arrived_at = shift.datetime_start;
      } else if (!isPds) {
        params.datetime_end = shift.datetime_end;
        params.datetime_leaved_at = shift.datetime_end;
      }

      if (device?.id) {
        const { platform, deviceType, deviceName, deviceModel, deviceVendor, osName, browser } = window.env;

        params.device = {
          id: device.id,
          platform,
          type: deviceType,
          deviceName,
          vendor: deviceVendor,
          model: deviceModel,
          browser,
          os: osName,
          position: {
            latitude: position?.latitude ? position.latitude : null,
            longitude: position?.longitude ? position.longitude : null
          }
        };
      }

      const result = await socket.put('/services', params);

      dispatch(slice.actions.validatePdsFds(result));
      // Rafraîchit la timeline MCE pour l'utilisateur qui vient de pointer
      // (le listener socket couvre les autres clients, mais l'émetteur ne
      // reçoit pas toujours son propre broadcast).
      queryClient.invalidateQueries({ queryKey: MCE_EVENTS_QUERY_KEY });
      callback(null, result.shift);
    } catch (error) {
      let errorMessage = 'Une erreur est survenue, veuillez réessayer ultérieurement.';

      if (error?.errors) {
        if (Array.isArray(error.errors)) {
          errorMessage = error.errors[0] || errorMessage;
        } else if (typeof error.errors === 'string') {
          errorMessage = error.errors;
        } else if (error.errors.comment) {
          errorMessage = error.errors.comment[0] || error.errors.comment;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      callback({ message: errorMessage });
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function validateArrivedAt({ shift, datetimeArrivedAt, managerId, callback }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoadingClocking());
    try {
      const result = await socket.put('/services/manager', {
        id: shift.id,
        datetime_arrived_at: datetimeArrivedAt,
        type: CLOCKING_TYPES.CLOCK_IN,
        vacation_id: shift.id,
        manager_id: managerId
      });
      dispatch(slice.actions.validateArrivedAt(result));
      callback(null, result.shift);
    } catch (error) {
      let errorMessage = 'Une erreur est survenue, veuillez réessayer ultérieurement.';

      if (error?.errors) {
        if (Array.isArray(error.errors)) {
          errorMessage = error.errors[0] || errorMessage;
        } else if (typeof error.errors === 'string') {
          errorMessage = error.errors;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      callback({ message: errorMessage });
      dispatch(slice.actions.hasError(error));
    }
  };
}
